import React, { useState } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { NavigationService } from '@/services/navigationService';
import { ApiError } from '@/lib/api-request';
import {
  approveProposal, dismissSituation, fetchSituation, retryStep, updateSuggestion,
  type HistoryEvent, type Situation, type Step,
} from './api';
import { PRIORITY_LABEL, eventText, formatDuration, formatWhen, resolvedReasonLabel } from './format';

const MAX_INSTRUCTION = 1000;
const MAX_BODY = 1500;
const STEP_STATUS: Record<Step['status'], string> = {
  proposed: 'Waiting for approval', approved: 'Approved', running: 'Running', succeeded: 'Done', failed: 'Failed', skipped: 'Skipped',
};

function EntityLink({ type, id, children }: { type: string; id: string; children: React.ReactNode }) {
  const inRouter = useInRouterContext();
  if (!inRouter) return <>{children}</>;
  const to = NavigationService.linkFor(type, id);
  return <Link to={{ pathname: to.pathname, search: to.search }} state={to.state}>{children}</Link>;
}

interface Props {
  item: Situation;
  expanded: boolean;
  onToggle: () => void;
  timeZone: string | null;
  /** The situation changed (approved, updated, dismissed, retried): the page puts the fresh one in place and reloads the lists. */
  onChanged: (item: Situation) => void;
  /** The card is in the "handled" list: read-only. */
  handled?: boolean;
  /** The suggestion changed since the user last looked (shown as a small badge when collapsed). */
  isNew?: boolean;
  /** Desktop detail panel: no collapsed header, always open (the list row lives in the page). */
  stage?: boolean;
}

/**
 * One situation: "Vendor has not responded". Collapsed it shows only what is verifiable (project, record, how long ago the message was sent, how long
 * without a reply) and the recommended action's catalog label. Expanded it shows the evidence, the steps that would run, and the controls: edit the
 * message, approve, update the suggestion with an instruction, dismiss, retry a failed step, see the history. Nothing is sent without Approve.
 */
export function SituationCard({ item, expanded, onToggle, timeZone, onChanged, handled = false, isNew = false, stage = false }: Props) {
  const proposal = item.proposal;
  const steps = proposal ? proposal.steps : [];
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<null | 'approve' | 'update' | 'dismiss' | string>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpdate, setShowUpdate] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [clarification, setClarification] = useState<string | null>(null);
  const [replaced, setReplaced] = useState<{ version: number; summary: string; new_version: number } | null>(null);
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [history, setHistory] = useState<HistoryEvent[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const open = item.status === 'open';
  const pending = open && !!proposal && proposal.status === 'proposed' && steps.length > 0;
  const bodyOf = (step: Step) => (drafts[step.id] !== undefined ? drafts[step.id] : (step.params.body as string) || '');
  const emptyBody = pending && steps.some((s) => s.editable && bodyOf(s).trim() === '');
  const chip = proposal && steps.length > 0 && proposal.status === 'proposed' ? steps[0].label : null;

  const fail = (e: unknown) => {
    const message = e instanceof Error ? e.message : 'Something went wrong';
    setError(message);
    // a stale / already-handled suggestion: show the latest version instead of leaving the old one on screen
    if (e instanceof ApiError && ['stale_proposal', 'already_approved', 'not_open'].includes(e.code || '')) {
      fetchSituation(item.id).then(onChanged).catch(() => undefined);
    }
  };

  const approve = async () => {
    if (!proposal) return;
    setBusy('approve');
    setError(null);
    try {
      onChanged(await approveProposal(proposal.id, proposal.version, steps.filter((s) => s.editable).map((s) => ({ id: s.id, body: bodyOf(s) }))));
      setDrafts({});
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const update = async () => {
    if (!proposal) return;
    setBusy('update');
    setError(null);
    setClarification(null);
    try {
      const result = await updateSuggestion(item.id, instruction.trim(), proposal.version);
      if (result.clarification) {
        setClarification(result.clarification);
        return;
      }
      setReplaced(result.replaced);
      setDrafts({});
      setInstruction('');
      setShowUpdate(false);
      onChanged(result.item);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async () => {
    setBusy('dismiss');
    setError(null);
    try {
      onChanged(await dismissSituation(item.id, dismissReason.trim() || undefined));
      setConfirmDismiss(false);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const retry = async (step: Step) => {
    setBusy(step.id);
    setError(null);
    try {
      onChanged(await retryStep(step.id));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const loadHistory = async () => {
    if (history) { setHistory(null); return; }
    setHistoryError(null);
    try {
      setHistory((await fetchSituation(item.id)).history || []);
    } catch (e) {
      setHistoryError(e instanceof Error ? e.message : 'The history could not be loaded');
    }
  };

  const sentAgo = `Sent ${formatDuration(item.hours_waiting)} ago`;
  const subtitle = [item.project_name, item.entity.label, handled ? resolvedReasonLabel(item.status === 'dismissed' ? 'dismissed' : item.resolved_reason) : `${sentAgo} · no reply`].filter(Boolean).join(' · ');

  const body = (
    <div className={`bcc-body${stage ? ' bcc-body-stage' : ''}`}>
      <section aria-label="Evidence">
        <p className="bcc-label">Evidence</p>
        <dl className="bcc-facts">
          <div className="bcc-fact"><dt>Record</dt><dd><EntityLink type={item.entity.type} id={item.entity.id}>{item.entity.label}</EntityLink></dd></div>
          <div className="bcc-fact"><dt>Vendor</dt><dd>{item.vendor.name}</dd></div>
          <div className="bcc-fact"><dt>Project</dt><dd>{item.project_name || '—'}</dd></div>
          <div className="bcc-fact"><dt>Waiting since</dt><dd>{formatWhen(item.waiting_since, timeZone)} ({formatDuration(item.hours_waiting)})</dd></div>
          <div className="bcc-fact"><dt>Follow-ups sent</dt><dd>{item.follow_up_count} of {item.evidence.follow_up_limit}</dd></div>
          <div className="bcc-fact"><dt>Raised after</dt><dd>{item.evidence.threshold_hours} hours without a reply</dd></div>
        </dl>
        {item.evidence.root_comment && (
          <blockquote className="bcc-quote">
            <cite>{item.follow_up_count > 0 ? 'First message' : 'Message'} · {item.evidence.root_comment.author || 'Someone on the team'} · {formatWhen(item.evidence.root_comment.at, timeZone)}</cite>
            {item.evidence.root_comment.text}
          </blockquote>
        )}
        {item.evidence.latest_comment && item.evidence.root_comment && item.evidence.latest_comment.id !== item.evidence.root_comment.id && (
          <blockquote className="bcc-quote">
            <cite>Latest message · {item.evidence.latest_comment.author || 'Someone on the team'} · {formatWhen(item.evidence.latest_comment.at, timeZone)}</cite>
            {item.evidence.latest_comment.text}
          </blockquote>
        )}
      </section>

      {proposal && (
        <section className="bcc-plan" aria-label="Suggested actions">
          <div className="bcc-plan-head">
            <p className="bcc-label" style={{ margin: 0 }}>{pending ? 'Actions to be executed' : steps.length > 0 ? 'Actions' : 'Suggestion'}</p>
            <span className="bcc-version">Version {proposal.version} · {proposal.source === 'ai' ? 'from your instruction' : 'prepared from the evidence'}</span>
          </div>
          {replaced && <div className="bcc-replaced" role="status">This suggestion replaced version {replaced.version} (&ldquo;{replaced.summary}&rdquo;). Review it before approving.</div>}
          {proposal.instruction && <p className="bcc-note">Your instruction: &ldquo;{proposal.instruction}&rdquo;</p>}
          <p style={{ margin: 0 }}>{proposal.summary}</p>
          {proposal.blocked && <p className="bcc-blocked">{item.follow_up_count} follow-ups have gone unanswered, so Bob does not propose another message. Contact the vendor directly (phone or in person).</p>}

          {steps.map((step) => (
            <div key={step.id} className="bcc-step" data-testid="plan-step">
              <div className="bcc-step-head">
                <span className="bcc-step-num">{step.seq}</span>
                <span className="bcc-step-label">{step.label}</span>
                <span className={`bcc-step-status bcc-st-${step.status}`}>{STEP_STATUS[step.status]}</span>
              </div>
              {step.editable ? (
                <>
                  <label className="sr-only" htmlFor={`step-${step.id}`}>Message for step {step.seq}</label>
                  <Textarea id={`step-${step.id}`} className="bcc-step-text" value={bodyOf(step)} maxLength={MAX_BODY} disabled={busy !== null || !open} onChange={(e) => setDrafts((d) => ({ ...d, [step.id]: e.target.value }))} />
                  <div className="bcc-count">{bodyOf(step).length} / {MAX_BODY}</div>
                  {step.effects && <p className="bcc-step-effects">{step.effects} What you see here is exactly what will be sent.</p>}
                </>
              ) : (
                <blockquote className="bcc-quote" style={{ margin: 0 }}>{step.params.body}</blockquote>
              )}
              {step.status === 'succeeded' && step.result && (
                <p className="bcc-step-result">Posted {step.result.posted_at ? formatWhen(step.result.posted_at, timeZone) : ''} in the vendor thread of <EntityLink type={item.entity.type} id={item.entity.id}>{item.entity.label}</EntityLink>. The vendor was notified through the usual comment notification.</p>
              )}
              {(step.status === 'failed' || step.status === 'skipped') && step.error && <p className="bcc-step-error" role="alert">{step.error}</p>}
              {step.status === 'failed' && open && (
                <div className="bcc-actions"><button type="button" className="bcc-act bcc-act-soft" disabled={busy !== null} onClick={() => void retry(step)}>{busy === step.id ? 'Retrying…' : 'Retry this step'}</button></div>
              )}
            </div>
          ))}
        </section>
      )}

      {error && <p className="bcc-inline-error" role="alert">{error}</p>}

      {pending && (
        <div className="bcc-actions">
          <button type="button" className="bcc-act bcc-act-primary" disabled={busy !== null || emptyBody} onClick={() => void approve()}>
            {busy === 'approve' ? 'Sending…' : steps.length === 1 ? `Approve: ${steps[0].label}` : 'Approve plan'}
          </button>
          <button type="button" className="bcc-act bcc-act-soft" disabled={busy !== null} onClick={() => { setShowUpdate((v) => !v); setClarification(null); }}>Update suggestion</button>
          <span className="bcc-spacer" />
          {!confirmDismiss && <button type="button" className="bcc-link-btn" disabled={busy !== null} onClick={() => setConfirmDismiss(true)}>Dismiss</button>}
        </div>
      )}
      {open && !pending && (
        <div className="bcc-actions">
          <span className="bcc-spacer" />
          {!confirmDismiss && <button type="button" className="bcc-link-btn" disabled={busy !== null} onClick={() => setConfirmDismiss(true)}>Dismiss</button>}
        </div>
      )}

      {confirmDismiss && open && (
        <div className="bcc-update">
          <label htmlFor={`dismiss-${item.id}`} className="bcc-label" style={{ margin: 0 }}>Dismiss this situation? (optional reason)</label>
          <input id={`dismiss-${item.id}`} className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" maxLength={300} value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} placeholder="e.g. handled by phone" />
          <p className="bcc-note">It comes back only if a newer comment to this vendor is left unanswered.</p>
          <div className="bcc-actions">
            <button type="button" className="bcc-act bcc-act-primary" disabled={busy !== null} onClick={() => void dismiss()}>{busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}</button>
            <button type="button" className="bcc-act bcc-act-soft" disabled={busy !== null} onClick={() => setConfirmDismiss(false)}>Cancel</button>
          </div>
        </div>
      )}

      {showUpdate && open && proposal && proposal.status === 'proposed' && (
        <div className="bcc-update" data-testid="update-panel">
          <label htmlFor={`instruction-${item.id}`} className="bcc-label" style={{ margin: 0 }}>What would you rather do?</label>
          <Textarea id={`instruction-${item.id}`} value={instruction} maxLength={MAX_INSTRUCTION} disabled={busy !== null} onChange={(e) => setInstruction(e.target.value)} placeholder="e.g. Ask first whether he can work during the weekend" />
          <div className="bcc-count">{instruction.length} / {MAX_INSTRUCTION}</div>
          <p className="bcc-note">This uses AI (it counts toward your AI usage). Bob will propose new steps using only the actions available here, and nothing is sent until you approve it.</p>
          {clarification && <div className="bcc-clarify" role="status">{clarification}</div>}
          <div className="bcc-actions">
            <button type="button" className="bcc-act bcc-act-primary" disabled={busy !== null || instruction.trim().length < 3} onClick={() => void update()}>{busy === 'update' ? 'Bob is updating the suggestion…' : 'Update suggestion'}</button>
            <button type="button" className="bcc-act bcc-act-soft" disabled={busy !== null} onClick={() => setShowUpdate(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!open && (
        <p className="bcc-note">
          {item.status === 'dismissed' ? `Dismissed${item.dismissed_reason ? `: ${item.dismissed_reason}` : ''}.` : `${resolvedReasonLabel(item.resolved_reason)}${item.resolved_at ? ` · ${formatWhen(item.resolved_at, timeZone)}` : ''}.`}
        </p>
      )}

      <div>
        <button type="button" className="bcc-link-btn" aria-expanded={!!history} onClick={() => void loadHistory()}>{history ? 'Hide history' : 'Show history'}</button>
        {historyError && <span className="bcc-inline-error" role="alert"> {historyError}</span>}
        {history && (
          <ul className="bcc-history" aria-label="History" style={{ marginTop: '0.5rem' }}>
            {history.length === 0 && <li>No history yet.</li>}
            {history.map((event) => (
              <li key={event.id}><time>{formatWhen(event.at, timeZone)}</time>{eventText(event, item.entity.label)}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  if (stage) {
    return (
      <article className="bcc-focus" data-testid="situation-card" data-item-id={item.id}>
        <header className="bcc-focus-meta">
          <span className="bcc-kind">
            Vendor has not responded
            {item.project_name && <><span className="bcc-sep" aria-hidden="true" />{item.project_name}</>}
          </span>
          <span className={`bcc-prio-label bcc-prio-label-${item.priority}`}>{PRIORITY_LABEL[item.priority]}</span>
        </header>
        <p className="bcc-focus-title">{item.title}{isNew && <span className="bcc-new">Updated</span>}</p>
        <p className="bcc-focus-sub">{subtitle}</p>
        {body}
      </article>
    );
  }

  return (
    <li className={`bcc-card${expanded ? ' is-open' : ''}${handled ? ' is-handled' : ''}`} data-testid="situation-card" data-item-id={item.id}>
      <button type="button" className="bcc-card-head" aria-expanded={expanded} onClick={onToggle}>
        <span className={`bcc-prio bcc-prio-${item.priority}`} role="img" aria-label={PRIORITY_LABEL[item.priority]} />
        <span className="bcc-card-main">
          <span className="bcc-card-title">{item.title}{isNew && <span className="bcc-new">Updated</span>}</span>
          <span className="bcc-card-sub">{subtitle}</span>
        </span>
        {handled
          ? <span className="bcc-chip bcc-chip-done">{item.status === 'dismissed' ? 'Dismissed' : 'Handled'}</span>
          : chip
            ? <span className="bcc-chip">{chip}</span>
            : <span className="bcc-chip bcc-chip-muted">{proposal?.blocked ? 'Contact the vendor directly' : STEP_STATUS[(steps[0]?.status) || 'proposed']}</span>}
        <ChevronDown className="bcc-chev h-4 w-4" aria-hidden />
      </button>

      {expanded && body}
    </li>
  );
}
